import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";
import DashboardPageTitle from "../../Components/InnerPageTitle";
import { Button, Card, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";

import "../../Assets/scss/pages/settings.scss";
import NoRecordFound from "../../Components/NoRecordFound";
import { useEffect, useMemo, useState } from "react";
import { UserCardPropTypes } from "../../PropTypes/CardListPropTypes";

// Card Logo
import Visa from "../../Assets/img/card/001-visa.svg";
import AmericanExpress from "../../Assets/img/card/002-american-express.svg";
import Discover from "../../Assets/img/card/007-discover.svg";
import JCB from "../../Assets/img/card/005-jcb.svg";
import MasterCard from "../../Assets/img/card/004-card.svg";
import DefaultCard from "../../Assets/img/card/003-credit-card.svg";
import {
  deleteCardService,
  getCardListService,
  makeDefaultCardService,
} from "../../Services/CardServices";
import { Elements } from "@stripe/react-stripe-js";
import CardPopup from "./CardPopup";
import { loadStripe } from "@stripe/stripe-js";
import DashboardBlankPagesTemplate from "../../Template/DashboardBlankPagesTemplate";
import { useLocation, useNavigate } from "react-router-dom";
import { SET_MESSAGE } from "../../Actions/Types";
import Select from 'react-select';
import select2LangPropTypes from "../../PropTypes/select2LangPropTypes";
import { AddBillingAddressService, deleteBillingAddressService, getBillingAddressService, getCountryListService, makeDefaultBillingAddressService, UpdateBillingAddressService } from "../../Services/BillingAddressService";
import countryPropTypes from "../../PropTypes/select2LangPropTypes";
import countryAddressPropTypes from "../../PropTypes/countryPropTypes";
import VoiceCommandPage from "./VoiceCommand";
import darkSelectStyles from "../../Helpers/darkSelectStyles";
import { useTheme } from "../../Context/ThemeContext";
import FilterContentPage from "./FilterContent";

// loads stripe
const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`);

// component to render settings page
const SettingsPage = () => {
  // Redux Data
  const { accessToken, currentWebsite, currentUserStatus, user } = useSelector(
    (store: StoreProptypes) => store
  );

  // Functions
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //theme
  const theme = useTheme()

  // useState
  const [cardListData, setCardListData] = useState<UserCardPropTypes[]>();
  const [billingAddressListData, setBillingAddressListData] = useState<any>();
  const [loadingCardList, setLoadingCardList] = useState(false);
  const [loadingBillingAddressList, setLoadingBillingAddressList] = useState(false);
  const [cardToken, setCardToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [vat, setVat] = useState("");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [country, setCountry] = useState<select2LangPropTypes>();
  const [countryList, setCountryList] = useState<select2LangPropTypes[]>([]);
  const [pincode, setPincode] = useState("");
  const location = useLocation();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  const isExpireCardUpdate = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('is_expired');
    }, [location.search]);

  useEffect(() => {
    if (accessToken) {
      // service to get card list
      getCardListService(accessToken, setCardListData, setLoadingCardList);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) {
      // service to get card list
      getBillingAddressService(accessToken, setBillingAddressListData, setLoadingBillingAddressList, dispatch, t);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) {
      // service to get card list
      getCountryListService(accessToken, setCountryList);
    }
  }, [accessToken]);

  useEffect(() => {
    if (
      currentWebsite?.platform === "wix" ||
      currentWebsite?.platform === "shopify" ||
      currentWebsite?.platform === "plesk"
    ) {
      navigate("/front");
    }
  }, [currentWebsite]);

  useEffect(() => {
    // check if user email exists
    if (user) {
        // check if admin user and plan is expired
        if (user?.department === 1 && user?.parent_id === 1) {
            setDisabledButton(true);
        } else {
            setDisabledButton(false);
        }
    }
  }, [user])

  useEffect(() => {
      if (isExpireCardUpdate === 'true') {
          setShowCardPopup(true);
          dispatch({
              type: SET_MESSAGE,
              payload: {
                  "toast_message": t('Your preferred payment card will be expired soon'),
                  "toast_type": "success"
              }
          })
      }
  }, [isExpireCardUpdate, navigate, location])

  // Show Card Popup
  const [showCardPopup, setShowCardPopup] = useState(false);
  const [showBillingAddressPopup, setShowBillingAddressPopup] = useState(false);
  const [showDeleteCardPopupLoading, setShowDeleteCardPopupLoading] =
    useState(false);
  const [showDeleteCardPopup, setDeleteCardPopup] = useState(false);
  const [showDeleteBillingAddressPopup, setDeleteBillingAddressPopup] = useState(false);
  const [deleteCardID, setDeleteCardID] = useState<number | null>();
  const [deleteBillingAddressID, setDeleteBillingAddressID] = useState<number | null>();
  const [editingAddress, setEditingAddress] = useState<any | null>(null);

  // function to close card popup
  const handleCloseCardPopup = () => {
    setShowCardPopup(false);
    setCardToken("");
  };

  // function to close billing address popup
  const handleCloseBillingAddressPopup = () => {
    setShowBillingAddressPopup(false);
    resetForm()
  };

  // function to show card popup
  const handleShowCardPopup = () => {
    setShowCardPopup(true);
  };

  // function to show billing address popup
  const handleShowBillingAddressPopup = () => {
    setShowBillingAddressPopup(true);
  };

  // function to close card delete popup
  const handleCloseDeleteCardPopup = () => {
    setDeleteCardPopup(false);
    setDeleteCardID(null);
  };

  // function to display delete card popup
  const handleShowDeleteCardPopup = (id: number) => {
    setDeleteCardPopup(true);
    id && setDeleteCardID(id);
  };

  /* const handleAddNewCard = () => {
        if (accessToken && cardToken !== '') {
            addNewCardService(accessToken, cardToken, setShowCardPopupLoading, setCardListData, setLoadingCardList, setShowCardPopup, dispatch);
        }
    } */

  // function to handle delete card
  const handleDeleteCard = () => {
    // check if access token and delete card ID are available
    if (accessToken && deleteCardID) {
      // service to perform delete card
      deleteCardService(
        accessToken,
        deleteCardID,
        setShowDeleteCardPopupLoading,
        setCardListData,
        setLoadingCardList,
        setDeleteCardPopup,
        dispatch,
        t
      );
    }
  };

  // function to display set default card popup
  const handleShowMakeDefaultCardPopup = (id: number) => {
    // check if access token and card ID and user confirmation are available,
    if (
      accessToken &&
      id &&
      window.confirm(
        t(
          "Selected card will be primary, update your subscription and all future transactions will be done from this card"
        ).toString()
      )
    ) {
      // service to set default card
      makeDefaultCardService(
        accessToken,
        id,
        setCardListData,
        setLoadingCardList,
        setDeleteCardPopup
      );
    }
  };

  function convertExpiryDateFormat(dateStr: string): string {
    try {
      const parts = dateStr.split("-");
      if (parts.length !== 3) {
        throw new Error("Date must be in YYYY-MM-DD format");
      }

      const year = parts[0].slice(-2); // Last two digits of the year
      const month = parts[1]; // Month part

      return `${month}/${year}`;
    } catch (error) {
      return `Error: ${(error as Error).message}`;
    }
  }

  const saveBillingAddress = (e: React.FormEvent) => {
    e.preventDefault();

    if (accessToken && (validate())) {
      if (editingAddress) {
        UpdateBillingAddressService(editingAddress.id, firstName ,lastName, streetAddress, country ? country?.value : '', pincode, accessToken, vat,setBillingAddressListData, setLoadingBillingAddressList, setShowBillingAddressPopup,  dispatch, t, resetForm)
      } else {
        AddBillingAddressService(firstName ,lastName, streetAddress, country ? country?.value : '', pincode, accessToken, vat,setBillingAddressListData, setLoadingBillingAddressList, setShowBillingAddressPopup,  dispatch, t, resetForm)
      }
    }

  }

  // function to display set default card popup
  const handleShowMakeDefaultBillingAddressPopup = (id: number) => {
    // check if access token and Billing Address and user confirmation are available,
    if (
      accessToken &&
      id
    ) {
      // service to set default card
      makeDefaultBillingAddressService(
        accessToken,
        id,
        setBillingAddressListData,
        setLoadingBillingAddressList,
        dispatch,
        t
      );
    }
  };

   // function to display delete card popup
  const handleShowDeleteBillingAddressPopup = (id: number) => {
    setDeleteBillingAddressPopup(true);
    id && setDeleteBillingAddressID(id);
  };

   // function to close card delete popup
  const handleCloseDeleteBillingAddressPopup = () => {
    setDeleteBillingAddressPopup(false);
    setDeleteBillingAddressID(null);
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = t("First Name is required");
    } else if (!/^[A-Za-z0-9\s'-]+$/.test(firstName)) {
      newErrors.firstName = t("First Name can only contain letters");
    } else if (firstName.length > 100) {
      newErrors.firstName = t("First Name cannot exceed 100 characters");
    }
    // Last Name
    if (!lastName.trim()) {
      newErrors.lastName = t("Last Name is required");
    } else if (!/^[A-Za-z0-9\s'-]+$/.test(lastName)) {
      newErrors.lastName = t("Last Name can only contain letters");
    }else if (lastName.length > 100) {
      newErrors.lastName = t("Last Name cannot exceed 100 characters");
    }

    if (!streetAddress.trim()) {
      newErrors.streetAddress = t("Street Address is required");
    } else if (!/^[A-Za-z0-9,\s'-]+$/.test(streetAddress)) {
      newErrors.streetAddress = t("Street Address can only contain letters");
    }else if (streetAddress.length > 255) {
      newErrors.streetAddress = t("Street Address cannot exceed 255 characters");
    }

    if (!country || typeof country.value !== "string"  || !country.value?.trim()) {
      newErrors.country = t("Country is required");
    }

    if (!String(pincode).trim()) {
      newErrors.pincode = t("Pincode is required");
    } else if (!/^[A-Za-z0-9]+$/.test(String(pincode))) {
      newErrors.pincode = t("Pincode can only contain letters and numbers");
    } else if (pincode.length < 4 || pincode.length > 10) {
      newErrors.pincode = t("Pincode must be 4–10 digits");
    }

    if (vat?.trim()) {
      if (!/^[A-Za-z0-9]+$/.test(vat)) {
        newErrors.vat = t("Vat can only contain letters and numbers");
      } else if (vat.length > 16) {
        newErrors.vat = t("Vat cannot exceed 16 characters");
      }
    }


    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // function to handle delete card
  const handleDeleteBillingAddress = () => {
    // check if access token and delete card ID are available
    if (accessToken && deleteBillingAddressID) {
      // service to perform delete card
      deleteBillingAddressService(
        accessToken,
        deleteBillingAddressID,
        setShowDeleteCardPopupLoading,
        setBillingAddressListData,
        setLoadingBillingAddressList,
        setDeleteBillingAddressPopup,
        dispatch,
        t
      );
    }
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setStreetAddress("");
    setCountry(undefined);
    setPincode("");
    setEditingAddress(null); // exit edit mode
    setVat('')
    setErrors({});
  };

  function splitName(fullName: string): { firstName: string; lastName: string } {
    const parts = fullName.trim().split(" ").filter(Boolean); // split on space, remove empty entries

    return {
      firstName: parts[0] || "",
      lastName: parts.length > 1 ? parts.slice(1).join(" ") : "" // if more than one word, join rest
    };
  }

  const handleEditBillingAddress = (id: number) => {
    const getSelectedIdData = billingAddressListData.find((d:any) => d.id === id)
    if (getSelectedIdData) {
      const { firstName, lastName } = splitName(getSelectedIdData.name);
      setFirstName(firstName)
      setLastName(lastName)
      setStreetAddress(getSelectedIdData.address)
      setCountry(countryList.find((d) => d.value == getSelectedIdData.country))
      setPincode(getSelectedIdData.pincode.toString())
      setVat(getSelectedIdData.vat_tax)
      setEditingAddress(getSelectedIdData)
      handleShowBillingAddressPopup()
    }
  }

  return (
    <>
      {currentUserStatus && (
        <>
          {currentUserStatus.isInitUser ? (
            <>
              <DashboardBlankPagesTemplate>
                <div className="aioa_dashboard-blank-pages-content-inner-wrapper">
                  <DashboardPageTitle pageTitle={`${t("Settings")}`} />

                  {currentWebsite &&
                  (currentWebsite.platform === "wix" ||
                    currentWebsite.platform === "shopify" ||
                    currentWebsite.platform === "Opencart-Pro" ||
                    currentWebsite.platform === "Magento-Pro") ? (
                    <></>
                  ) : (
                    <>
                      <div className="manage-cards">
                        {/* FOR CARD */}
                        <Card>
                          <Card.Header>
                            <h2 className="mb-0 h5">{t("Card Details")}</h2>
                            <Button
                              variant="primary"
                              onClick={handleShowCardPopup}
                              disabled={disabledButton}
                              size="lg"
                              className="aioa_dashboard-icon-btn"
                            >
                              <div className="aioa_dashboard-btn-icon">
                                <i
                                  className="material-symbols-outlined"
                                  aria-hidden="true"
                                >
                                  add_card
                                </i>
                              </div>
                              <div className="aioa_dashboard-btn-text">
                                {t("Add New Card")}
                              </div>
                            </Button>
                          </Card.Header>

                          <Card.Body>
                            <div className="credit-card-list">
                              {cardListData ? (
                                <>
                                  {cardListData.length > 0 ? (
                                    <>
                                      <Row xl={2}>
                                        {cardListData.map((cardItem) => {
                                          return (
                                            <Col key={cardItem.id}>
                                              <div
                                                className={`credit-card-box ${
                                                  cardItem.is_default === 1
                                                    ? `active`
                                                    : ""
                                                }`}
                                              >
                                                <div
                                                  className={`credit-card-details`}
                                                >
                                                  <div className="credit-card-img-box">
                                                    {cardItem.card_type.toLowerCase() ===
                                                    "visa" ? (
                                                      <>
                                                        <img
                                                          src={Visa}
                                                          alt="Visa"
                                                        />
                                                      </>
                                                    ) : cardItem.card_type.toLowerCase() ===
                                                      "mastercard" ? (
                                                      <>
                                                        <img
                                                          src={MasterCard}
                                                          alt="MasterCard"
                                                        />
                                                      </>
                                                    ) : cardItem.card_type.toLowerCase() ===
                                                      "discover" ? (
                                                      <>
                                                        <img
                                                          src={Discover}
                                                          alt="Discover"
                                                        />
                                                      </>
                                                    ) : cardItem.card_type.toLowerCase() ===
                                                      "american express" ? (
                                                      <>
                                                        <img
                                                          src={AmericanExpress}
                                                          alt="American Express"
                                                        />
                                                      </>
                                                    ) : cardItem.card_type.toLowerCase() ===
                                                      "jcb" ? (
                                                      <>
                                                        <img
                                                          src={JCB}
                                                          alt="JCB"
                                                        />
                                                      </>
                                                    ) : (
                                                      <>
                                                        <img
                                                          src={DefaultCard}
                                                          alt="Card"
                                                        />
                                                      </>
                                                    )}
                                                  </div>

                                                  <div className="credit-card-detail-box">
                                                    <span>
                                                      <strong>
                                                        {cardItem.card_type}
                                                      </strong>
                                                    </span>
                                                    <span>
                                                      <strong>
                                                        XXXX-XXXX-XXXX-
                                                        {cardItem.last_digits}
                                                      </strong>
                                                    </span>
                                                    <span>
                                                      Expires{" "}
                                                      {convertExpiryDateFormat(
                                                        cardItem.exp_date
                                                      )}
                                                    </span>
                                                  </div>
                                                </div>

                                                {cardItem.is_default !== 1 ? (
                                                  <>
                                                    <div className="action-btns">
                                                      <Button
                                                        variant="link"
                                                        onClick={() =>
                                                          handleShowMakeDefaultCardPopup(
                                                            cardItem.id
                                                          )
                                                        }
                                                      >
                                                        {t(
                                                          "Make This Address Primary"
                                                        )}
                                                      </Button>
                                                      <Button
                                                        variant="danger"
                                                        aria-label={t(
                                                          "Delete Card"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleShowDeleteBillingAddressPopup(
                                                            cardItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          delete
                                                        </i>
                                                      </Button>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="action-btns">
                                                      <strong className="text-primary me-2">
                                                        {t("Primary Address")}
                                                      </strong>
                                                      <span className="btn btn-success mb-0 p-0">
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          check
                                                        </i>
                                                      </span>
                                                      
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            </Col>
                                          );
                                        })}
                                      </Row>
                                    </>
                                  ) : (
                                    <>
                                      <div className="w-100">
                                        <NoRecordFound
                                          imageType={`card`}
                                          joyful={false}
                                          noRecordText={`No Card Found.`}
                                        />
                                      </div>
                                    </>
                                  )}
                                </>
                              ) : (
                                <></>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                        {/* FOR BILLING ADDRESS */}
                        <Card className="mt-3">
                        <Card.Header>
                          <h2 className="mb-0 h5">{t("Billing Address")}</h2>
                          <Button
                            variant="primary"
                            onClick={handleShowBillingAddressPopup}
                            size="lg"
                            disabled={disabledButton}
                            className="aioa_dashboard-icon-btn"
                          >
                            <div className="aioa_dashboard-btn-icon">
                              <i
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                add_home
                              </i>
                            </div>
                            <div className="aioa_dashboard-btn-text">
                              {t("Add New Billing Address")}
                            </div>
                          </Button>
                        </Card.Header>

                        <Card.Body>
                          <div className="credit-card-list">
                            {billingAddressListData ? (
                              <>
                                {billingAddressListData.length > 0 ? (
                                  <>
                                    <Row xl={2}>
                                      {billingAddressListData.map((addressItem:any) => {
                                        return (
                                          <Col key={addressItem.id}>
                                            <div
                                              className={`credit-card-box ${
                                                addressItem.is_default === 1
                                                  ? `active`
                                                  : ""
                                              }`}
                                            >
                                              <div
                                                className={`credit-card-details`}
                                              >
                                                <div className="credit-card-detail-box">
                                                  <span>
                                                    <strong>
                                                      {addressItem.name}
                                                    </strong>
                                                  </span>
                                                  {addressItem?.vat_tax && (
                                                    <span>
                                                        {addressItem.vat_tax}
                                                    </span>
                                                  )}
                                                  <span>
                                                      { addressItem.address.endsWith(',') ? addressItem.address.slice(0, -1) : addressItem.address }, {countryList.find((d) => d.value == addressItem.country)?.label} ,{addressItem.pincode}
                                                  </span>
                                                </div>
                                              </div>

                                              {addressItem.is_default !== 1 ? (
                                                <>
                                                  <div className="action-btns">
                                                    <Button
                                                      variant="link"
                                                      onClick={() =>
                                                        handleShowMakeDefaultBillingAddressPopup(
                                                          addressItem.id
                                                        )
                                                      }
                                                    >
                                                      {t(
                                                        "Make This Address Primary"
                                                      )}
                                                    </Button>
                                                      <Button
                                                        variant="primary"
                                                        className="ms-3"
                                                        aria-label={t(
                                                          "Edit Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleEditBillingAddress(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          edit
                                                        </i>
                                                      </Button>

                                                      <Button
                                                        variant="danger"
                                                        aria-label={t(
                                                          "Delete Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleShowDeleteBillingAddressPopup(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          delete
                                                        </i>
                                                      </Button>
                                                    </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="action-btns">
                                                    <strong className="text-primary me-2">
                                                      {t("Primary Address")}
                                                    </strong>
                                                    <span className="btn btn-success mb-0 p-0">
                                                      <i
                                                        className={`material-symbols-outlined`}
                                                        aria-hidden="true"
                                                      >
                                                        check
                                                      </i>
                                                    </span>
                                                    <Button
                                                        variant="primary"
                                                        className="ms-3"
                                                        aria-label={t(
                                                          "Edit Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleEditBillingAddress(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          edit
                                                        </i>
                                                      </Button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </Col>
                                        );
                                      })}
                                    </Row>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-100">
                                      <NoRecordFound
                                        imageType={`card`}
                                        joyful={false}
                                        noRecordText={`No Card Found.`}
                                      />
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </Card.Body>
                        </Card>
                      </div>
                    </>
                  )}

                  <VoiceCommandPage disabledButton={disabledButton}/>
                  <FilterContentPage disabledButton={disabledButton}/>
                </div>
              </DashboardBlankPagesTemplate>
            </>
          ) : (
            <>
              <DashboardPagesTemplate>
                <DashboardPageTitle pageTitle={`${t("Settings")}`} />

                {/* {currentWebsite && ((currentWebsite.platform === "wix" || currentWebsite.platform === 'shopify' || currentWebsite.platform === "Opencart-Pro" || currentWebsite.platform === "Magento-Pro")) ? (
                                    <>

                                    </>
                                ) : (
                                    <>
                                        {currentWebsite && currentWebsitePlanStatus && (
                                            <>

                                                <div className="aioa_dashboard-manage-plan aioa_dashboard-settings-screen">
                                                    {currentWebsitePlanStatus.isTrial ? (
                                                        <>
                                                            <AllUserOnlyTrialPlanInfo />
                                                        </>
                                                    ) : currentWebsitePlanStatus.isFreeWidget ? (
                                                        <>
                                                            <FreeWidgetPlanInfo />
                                                        </>
                                                    ) : currentWebsitePlanStatus.isCanceledPlan ? (
                                                        <>
                                                            <AllUserOnlyCanceledPlanInfo />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <AllUserOnlyActivePlanInfo />
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )} */}

                {currentWebsite &&
                (currentWebsite.platform === "wix" ||
                  currentWebsite.platform === "shopify" ||
                  currentWebsite.platform === "Opencart-Pro" ||
                  currentWebsite.platform === "Magento-Pro") ? (
                  <></>
                ) : (
                  <>
                    <div className="manage-cards">
                      {/* FOR CARD */}
                      <Card>
                        <Card.Header>
                          <h2 className="mb-0 h5">{t("Card Details")}</h2>
                          <Button
                            variant="primary"
                            onClick={handleShowCardPopup}
                            size="lg"
                            disabled={disabledButton}
                            className="aioa_dashboard-icon-btn"
                          >
                            <div className="aioa_dashboard-btn-icon">
                              <i
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                add_card
                              </i>
                            </div>
                            <div className="aioa_dashboard-btn-text">
                              {t("Add New Card")}
                            </div>
                          </Button>
                        </Card.Header>

                        <Card.Body>
                          <div className="credit-card-list">
                            {cardListData ? (
                              <>
                                {cardListData.length > 0 ? (
                                  <>
                                    <Row xl={2}>
                                      {cardListData.map((cardItem) => {
                                        return (
                                          <Col key={cardItem.id}>
                                            <div
                                              className={`credit-card-box ${
                                                cardItem.is_default === 1
                                                  ? `active`
                                                  : ""
                                              }`}
                                            >
                                              <div
                                                className={`credit-card-details`}
                                              >
                                                <div className="credit-card-img-box">
                                                  {cardItem.card_type.toLowerCase() ===
                                                  "visa" ? (
                                                    <>
                                                      <img
                                                        src={Visa}
                                                        alt="Visa"
                                                      />
                                                    </>
                                                  ) : cardItem.card_type.toLowerCase() ===
                                                    "mastercard" ? (
                                                    <>
                                                      <img
                                                        src={MasterCard}
                                                        alt="MasterCard"
                                                      />
                                                    </>
                                                  ) : cardItem.card_type.toLowerCase() ===
                                                    "discover" ? (
                                                    <>
                                                      <img
                                                        src={Discover}
                                                        alt="Discover"
                                                      />
                                                    </>
                                                  ) : cardItem.card_type.toLowerCase() ===
                                                    "american express" ? (
                                                    <>
                                                      <img
                                                        src={AmericanExpress}
                                                        alt="American Express"
                                                      />
                                                    </>
                                                  ) : cardItem.card_type.toLowerCase() ===
                                                    "jcb" ? (
                                                    <>
                                                      <img
                                                        src={JCB}
                                                        alt="JCB"
                                                      />
                                                    </>
                                                  ) : (
                                                    <>
                                                      <img
                                                        src={DefaultCard}
                                                        alt="Card"
                                                      />
                                                    </>
                                                  )}
                                                </div>

                                                <div className="credit-card-detail-box">
                                                  <span>
                                                    <strong>
                                                      {cardItem.card_type}
                                                    </strong>
                                                  </span>
                                                  <span>
                                                    <strong>
                                                      XXXX-XXXX-XXXX-
                                                      {cardItem.last_digits}
                                                    </strong>
                                                  </span>
                                                  <span>
                                                    Expires{" "}
                                                    {convertExpiryDateFormat(
                                                      cardItem.exp_date
                                                    )}
                                                  </span>
                                                </div>
                                              </div>

                                              {cardItem.is_default !== 1 ? (
                                                <>
                                                  <div className="action-btns">
                                                    <Button
                                                      variant="link"
                                                      onClick={() =>
                                                        handleShowMakeDefaultCardPopup(
                                                          cardItem.id
                                                        )
                                                      }
                                                    >
                                                      {t(
                                                        "Make This Card Primary"
                                                      )}
                                                    </Button>
                                                    <Button
                                                      variant="danger"
                                                      aria-label={t(
                                                        "Delete Card"
                                                      ).toString()}
                                                      onClick={() =>
                                                        handleShowDeleteCardPopup(
                                                          cardItem.id
                                                        )
                                                      }
                                                    >
                                                      <i
                                                        className={`material-symbols-outlined`}
                                                        aria-hidden="true"
                                                      >
                                                        delete
                                                      </i>
                                                    </Button>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="action-btns">
                                                    <strong className="text-primary me-2">
                                                      {t("Primary card")}
                                                    </strong>
                                                    <span className="btn btn-success mb-0 p-0">
                                                      <i
                                                        className={`material-symbols-outlined`}
                                                        aria-hidden="true"
                                                      >
                                                        check
                                                      </i>
                                                    </span>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </Col>
                                        );
                                      })}
                                    </Row>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-100">
                                      <NoRecordFound
                                        imageType={`card`}
                                        joyful={false}
                                        noRecordText={`No Card Found.`}
                                      />
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                      {/* FOR BILLING ADDRESS */}
                      <Card className="mt-3">
                        <Card.Header>
                          <h2 className="mb-0 h5">{t("Billing Address")}</h2>
                          <Button
                            variant="primary"
                            onClick={handleShowBillingAddressPopup}
                            size="lg"
                            disabled={disabledButton}
                            className="aioa_dashboard-icon-btn"
                          >
                            <div className="aioa_dashboard-btn-icon">
                              <i
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                add_home
                              </i>
                            </div>
                            <div className="aioa_dashboard-btn-text">
                              {t("Add New Billing Address")}
                            </div>
                          </Button>
                        </Card.Header>

                        <Card.Body>
                          <div className="credit-card-list">
                            {billingAddressListData ? (
                              <>
                                {billingAddressListData.length > 0 ? (
                                  <>
                                    <Row xl={2}>
                                      {billingAddressListData.map((addressItem:any) => {
                                        return (
                                          <Col key={addressItem.id}>
                                            <div
                                              className={`credit-card-box ${
                                                addressItem.is_default === 1
                                                  ? `active`
                                                  : ""
                                              }`}
                                            >
                                              <div
                                                className={`credit-card-details`}
                                              >
                                                <div className="credit-card-detail-box">
                                                  <span>
                                                    <strong className="ellipsis">
                                                      {addressItem.name}
                                                    </strong>
                                                  </span>
                                                  {addressItem?.vat_tax && (
                                                    <span>
                                                        {addressItem.vat_tax}
                                                    </span>
                                                  )}
                                                  <span className="ellipsis">
                                                      { addressItem.address.endsWith(',') ? addressItem.address.slice(0, -1) : addressItem.address }, {countryList.find((d) => d.value == addressItem.country)?.label} ,{addressItem.pincode}
                                                  </span>
                                                </div>
                                              </div>

                                              {addressItem.is_default !== 1 ? (
                                                <>
                                                  <div className="action-btns">
                                                    <Button
                                                      variant="link"
                                                      onClick={() =>
                                                        handleShowMakeDefaultBillingAddressPopup(
                                                          addressItem.id
                                                        )
                                                      }
                                                    >
                                                      {t(
                                                        "Make This Address Primary"
                                                      )}
                                                    </Button>
                                                      <Button
                                                        variant="primary"
                                                        className="ms-3"
                                                        aria-label={t(
                                                          "Edit Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleEditBillingAddress(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          edit
                                                        </i>
                                                      </Button>

                                                      <Button
                                                        variant="danger"
                                                        aria-label={t(
                                                          "Delete Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleShowDeleteBillingAddressPopup(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          delete
                                                        </i>
                                                      </Button>
                                                    </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="action-btns">
                                                    <strong className="text-primary me-2">
                                                      {t("Primary Address")}
                                                    </strong>
                                                    <span className="btn btn-success mb-0 p-0">
                                                      <i
                                                        className={`material-symbols-outlined`}
                                                        aria-hidden="true"
                                                      >
                                                        check
                                                      </i>
                                                    </span>
                                                       <Button
                                                        variant="primary"
                                                        className="ms-3"
                                                        aria-label={t(
                                                          "Edit Billing Address"
                                                        ).toString()}
                                                        onClick={() =>
                                                          handleEditBillingAddress(
                                                            addressItem.id
                                                          )
                                                        }
                                                      >
                                                        <i
                                                          className={`material-symbols-outlined`}
                                                          aria-hidden="true"
                                                        >
                                                          edit
                                                        </i>
                                                      </Button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </Col>
                                        );
                                      })}
                                    </Row>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-100">
                                      <NoRecordFound
                                        imageType={`card`}
                                        joyful={false}
                                        noRecordText={`No Card Found.`}
                                      />
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </>
                )}

                <VoiceCommandPage disabledButton={disabledButton}/>
                <FilterContentPage disabledButton={disabledButton}/>
              </DashboardPagesTemplate>
            </>
          )}
        </>
      )}

      <Modal
        show={showCardPopup}
        onHide={handleCloseCardPopup}
        size="lg"
        fullscreen="lg-down"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Add New Card")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="card-input-group">
              <Elements stripe={stripePromise}>
                <CardPopup
                  setCardListData={setCardListData}
                  setLoadingCardList={setLoadingCardList}
                  setShowCardPopup={setShowCardPopup}
                />
              </Elements>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal
        show={showBillingAddressPopup}
        onHide={handleCloseBillingAddressPopup}
        size="lg"
        fullscreen="lg-down"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          {editingAddress ?
          <Modal.Title>{t("Update Billing Address")}</Modal.Title>
          :
          <Modal.Title>{t("Add New Billing Address")}</Modal.Title>
          }
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveBillingAddress}>
           <div className='card-input-group row'>
                <div className='col-6'>
                  <InputGroup className="mt-3 input-group-outline">
                    <Form.Control
                      placeholder={`${t("First Name")}`}
                      aria-label={`${t("First Name")}`}
                      type="text"
                      className="rounded-2"
                      size="lg"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                      value={firstName}
                      isInvalid={!!errors.firstName}
                      style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </InputGroup>

                </div>
                <div className='col-6'>
                  <InputGroup className="mt-3 input-group-outline">
                    <Form.Control
                      placeholder={`${t("Last Name")}`}
                      aria-label={`${t("Last Name")}`}
                      type="text"
                      className="rounded-2"
                      size="lg"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                      value={lastName}
                      isInvalid={!!errors.lastName}
                      style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </InputGroup>
                </div>
            </div>
           <div className='card-input-group row'>
                <div className='col-12'>
                  <InputGroup className="mt-3 input-group-outline">
                    <Form.Control
                      placeholder={`${t("Street Address")}`}
                      aria-label={`${t("Street Address")}`}
                      type="text"
                      className="rounded-2"
                      size="lg"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStreetAddress(e.target.value)}
                      value={streetAddress}
                      isInvalid={!!errors.streetAddress}
                      style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.streetAddress}
                    </Form.Control.Feedback>
                  </InputGroup>
                </div>
            </div>
           <div className='card-input-group row'>
                <div className='col-6 mt-3 h-100'>
                  <InputGroup className="mb-3 h-100 input-group-outline">
                    {/* <Form.Select required className="rounded-2" value={country} onChange={(e) => setCountry(e.currentTarget.value)} style={{ backgroundImage: "none", paddingRight: "0.75rem", height: '5vh'}} isInvalid={!!errors.country}>
                        {countryList && countryList.map(item => <option value={item.id}>{t(item.name)}</option>)}
                    </Form.Select> */}
                    <Select
                          id="language_select"
                          onChange={(e) => { setCountry({ label: e ? e.label : "", value: e ? String(e.value) : "" }) }}
                          options={countryList}
                          defaultValue={country}
                          value={country}
                          closeMenuOnSelect={true}
                          backspaceRemovesValue={true}
                          isClearable={false}
                          isSearchable={true}
                          placeholder={`${t('Select Country')}...`}
                          className={`aioa_dashboard-custom-country-select w-100 rounded-2 ${errors.country ? "is-invalid" : ""}`}
                          aria-label={t('Select Country')}
                          styles={theme.theme === 'dark' ? darkSelectStyles : {}}
                          classNamePrefix="lang-select"
                          theme={(theme) => ({
                            ...theme,
                            borderRadius: 0,
                            colors: {
                              ...theme.colors,
                              primary25: '#d9cce6',
                              primary: '#420083',
                            },
                          })}

                        />
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </InputGroup>
                </div>
                <div className='col-6'>
                  <InputGroup className="mt-3 input-group-outline">
                    <Form.Control
                      placeholder={`${t("Pincode")}`}
                      aria-label={`${t("Pincode")}`}
                      type="text"
                      className="rounded-2"
                      size="lg"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPincode(e.target.value)}
                      value={pincode}
                      isInvalid={!!errors.pincode}
                      style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.pincode}
                    </Form.Control.Feedback>
                  </InputGroup>
                </div>
            </div>
            <div className='card-input-group row'>
                <div className='col-6'>
                  <InputGroup className="input-group-outline">
                    <Form.Control
                      placeholder={`${t("VAT Number")}`}
                      aria-label={`${t("VAT Number")}`}
                      type="text"
                      className="rounded-2"
                      size="lg"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVat(e.target.value)}
                      value={vat}
                      isInvalid={!!errors.vat}
                      style={{ backgroundImage: "none", paddingRight: "0.75rem" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vat}
                    </Form.Control.Feedback>
                  </InputGroup>

                </div>
            </div>
            <div className='card-save-btns d-flex m-2 justify-content-center'>
                <Button variant="primary" type="submit">
                    {/* {showCardPopupLoading ? <Spinner animation="grow" size='sm' className='me-2' /> : <></>} */}
                    {editingAddress ?
                      <span>
                          {t('Update')}
                      </span>
                     :
                      <span>
                          {t('Save Button Label')}
                      </span>
                     }
                </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteCardPopup}
        onHide={handleCloseDeleteCardPopup}
        size="lg"
        fullscreen="lg-down"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete Card")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("Delete Card Popup Msg")}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDeleteCard}>
            {t("Delete Button Label")}
          </Button>
          <Button variant="danger" onClick={handleCloseDeleteCardPopup}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteBillingAddressPopup}
        onHide={handleCloseDeleteBillingAddressPopup}
        size="lg"
        fullscreen="lg-down"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete Billing Address")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("Are you sure want to delete billing address?")}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDeleteBillingAddress}>
            {t("Delete Button Label")}
          </Button>
          <Button variant="danger" onClick={handleCloseDeleteBillingAddressPopup}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SettingsPage;
